import {
  WAIT_TOLERANCE_MINUTES,
  PEER_WAIT_MARGIN_MINUTES,
  MAX_PLAUSIBLE_WAIT_HOURS,
  ON_TIME_LABEL,
  DELAYED_LABEL,
  NOT_COUNTED_LABEL
} from "@/utils/dealerWaitThresholds";
import { NO_WAIT_MINUTES, isCountedRoute } from "@/utils/dealerWaitRows";

export const ON_TIME_TONE = "green";
export const DELAYED_TONE = "red";
export const NOT_COUNTED_TONE = "gray";

const TEXT_START = 0;
const HOUR_AND_MINUTES_LENGTH = 5;
const OTHER_ROUTE_NAME = "otra ruta";
const WITHOUT_MARKS_EXPLANATION = "El repartidor no marcó su llegada o el inicio de ruta.";
const BLOCKED_BY_MV_EXPLANATION = "MV frenó la ruta mientras el repartidor esperaba.";
const ORDER_ADDED_BY_MV_EXPLANATION = "MV agregó un pedido cuando el repartidor ya estaba en la cocina.";
const DISCARDED_EXPLANATION = `Error de marca: la espera pasa de ${MAX_PLAUSIBLE_WAIT_HOURS} horas.`;
const LEFT_EARLY_EXPLANATION = "El repartidor salió antes de la hora programada.";
const NOT_COUNTED_EXPLANATION = "Esta espera no cuenta para el nivel.";
const LESS_THAN_ONE_MINUTE = "menos de 1";

const roundMinutes = (minutes) => Math.round(minutes);

const describeFirstRound = (row) => (row.first_round_route ? `la ${row.first_round_route}` : OTHER_ROUTE_NAME);

const explainNotCounted = (row) => {
  if (!row.has_marks) {
    return WITHOUT_MARKS_EXPLANATION;
  }
  if (row.is_blocked_by_mv) {
    return BLOCKED_BY_MV_EXPLANATION;
  }
  if (row.had_order_added_by_mv) {
    return ORDER_ADDED_BY_MV_EXPLANATION;
  }
  if (row.is_dealer_second_round) {
    return `El repartidor salió primero con ${describeFirstRound(row)} y volvió por esta ruta. La espera es del repartidor.`;
  }

  return row.is_discarded ? DISCARDED_EXPLANATION : NOT_COUNTED_EXPLANATION;
};

const explainPeerCap = (row) => {
  const countedMinutes = roundMinutes(row.counted_wait_minutes);
  const peerMinutes = countedMinutes - PEER_WAIT_MARGIN_MINUTES;
  const scheduledHour = row.scheduled_departure_time.slice(TEXT_START, HOUR_AND_MINUTES_LENGTH);

  return `Esperó ${roundMinutes(row.wait_minutes)} min, pero las otras rutas de las ${scheduledHour} esperaron ${peerMinutes}. A la cocina se le cuentan ${countedMinutes} min (${peerMinutes} + ${PEER_WAIT_MARGIN_MINUTES} de margen).`;
};

const explainKitchenLateRoute = (row) =>
  `Esperó ${roundMinutes(row.wait_minutes)} min. Esta ruta salió tarde ${row.kitchen_late_days} días en el corte, con ${row.kitchen_late_dealers} repartidores distintos: se cuenta completa.`;

const describeExcessMinutes = (excessMinutes) => {
  const roundedExcessMinutes = roundMinutes(excessMinutes);

  return roundedExcessMinutes === NO_WAIT_MINUTES ? LESS_THAN_ONE_MINUTE : roundedExcessMinutes;
};

const hasExcess = (row) => row.excess_minutes > NO_WAIT_MINUTES;

const explainCounted = (row) => {
  if (row.wait_minutes < NO_WAIT_MINUTES) {
    return LEFT_EARLY_EXPLANATION;
  }
  if (row.is_kitchen_late_route && hasExcess(row)) {
    return explainKitchenLateRoute(row);
  }
  if (row.is_capped_by_peers) {
    return explainPeerCap(row);
  }
  if (hasExcess(row)) {
    return `Esperó ${roundMinutes(row.wait_minutes)} min: ${describeExcessMinutes(row.excess_minutes)} más que los ${WAIT_TOLERANCE_MINUTES} de tolerancia.`;
  }

  return `Esperó ${roundMinutes(row.wait_minutes)} min, dentro de los ${WAIT_TOLERANCE_MINUTES} de tolerancia.`;
};

export const describeRouteStatus = (row) => {
  if (!isCountedRoute(row)) {
    return { label: NOT_COUNTED_LABEL, tone: NOT_COUNTED_TONE, explanation: explainNotCounted(row) };
  }

  return hasExcess(row)
    ? { label: DELAYED_LABEL, tone: DELAYED_TONE, explanation: explainCounted(row) }
    : { label: ON_TIME_LABEL, tone: ON_TIME_TONE, explanation: explainCounted(row) };
};
